import React, { useMemo, useEffect } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    useTheme,
    useMediaQuery,
    Typography,
    CircularProgress,
    Alert,
    Grid,
    Card,
    CardContent,
    IconButton,
    Tooltip,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getKayitIstatistikleri } from '../../../services/api/kayitIstatistikleriService';
import { KayitIstatistikleriError } from '../../../types/ErrorModals/KayitIstatistikleriError';
import { KayitIstatistikleriResponseModal, SezonKayitIstatistik } from '../../../types/modals';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { motion } from 'framer-motion';
import { useInstitution } from '../../../contexts/InstitutionContext';
import { getSezonOptions } from '../../../utils/sezon';

// Sezon renkleri: sezonOptions içindeki sıraya göre döngüsel olarak atanır
const SEZON_RENKLERI = ['#FF9800', '#2196F3', '#9C27B0', '#4CAF50', '#F44336', '#00BCD4'];

// Özet kart props tipi
interface SummaryCardProps {
    title: string;
    value: number;
    color: string;
    trendValue: number;
    icon?: React.ReactNode;
}

interface KayitIstatistikleriProps {
    // Dışarıdan (örn. DashboardPage'deki paylaşılan sezon seçicisi) verilirse
    // bileşen bu değere göre kontrol edilir ve kendi sezon seçicisini gizler.
    // Verilmezse bağımsız çalışır ve kendi ToggleButtonGroup'unu gösterir.
    sezon?: number | 'all';
}

const KayitIstatistikleri: React.FC<KayitIstatistikleriProps> = ({ sezon: sezonProp }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const sezonOptions = useMemo(() => getSezonOptions(), []);
    const [internalSeason, setInternalSeason] = React.useState<'all' | number>('all');
    const isControlled = sezonProp !== undefined;
    const selectedSeason = isControlled ? sezonProp : internalSeason;
    const setSelectedSeason = setInternalSeason;

    // Use the institution context to get the selected institution
    const { selectedSubInstitution, institution } = useInstitution();

    // Get the appropriate institution code based on selection
    const kurumKodu = useMemo(() => {
        // If a sub-institution is selected, use its code
        if (selectedSubInstitution) {
            return selectedSubInstitution.code;
        }
        // Otherwise use the main institution code
        if (institution.code)
            return institution.code;

        return localStorage.getItem('kurumKodu') || '';
    }, [selectedSubInstitution, institution]);

    // Add debug logging for troubleshooting
    useEffect(() => {
        console.log("KayitIstatistikleri component - institution context:", {
            institutionCode: institution.code,
            institutionName: institution.name,
            selectedSubInstitutionCode: selectedSubInstitution?.code,
            selectedSubInstitutionName: selectedSubInstitution?.name,
            derivedKurumKodu: kurumKodu
        });
    }, [institution, selectedSubInstitution, kurumKodu]);

    const { data, isLoading, error, refetch, isRefetching } = useQuery<KayitIstatistikleriResponseModal, Error>({
        queryKey: ['kayitIstatistikleri', kurumKodu, sezonOptions],
        queryFn: async () => {
            try {
                console.log("Fetching kayitIstatistikleri with kurumKodu:", kurumKodu, "sezonlar:", sezonOptions);
                return await getKayitIstatistikleri({
                    kurumKodu: kurumKodu,
                    sezonlar: sezonOptions
                });
            } catch (err) {
                const kayitError = err as KayitIstatistikleriError;
                throw new Error(kayitError.message);
            }
        },
        refetchInterval: 1000 * 60 * 15, // 15 dakikada bir yenile
        enabled: !!kurumKodu, // Only enable the query if we have a kurumKodu
        retry: 1,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 5 // 5 minutes
    });

    // Sezon -> istatistik eşlemesi (backend'den gelmeyen sezonlar için sıfır değerli varsayılan)
    const statsBySezon = useMemo(() => {
        const map = new Map<number, SezonKayitIstatistik>();
        (data?.sezonlar || []).forEach(s => map.set(s.sezon, s));
        return map;
    }, [data]);

    const getStat = (sezon: number): SezonKayitIstatistik =>
        statsBySezon.get(sezon) || {
            sezon,
            toplamYeniKayit: 0,
            gunlukYeniKayit: 0,
            haftalikYeniKayit: 0,
            aylikYeniKayit: 0,
            toplamKayitYenileme: 0,
            gunlukKayitYenileme: 0,
            haftalikKayitYenileme: 0,
            aylikKayitYenileme: 0,
        };

    // Ekranda gösterilecek sezonlar
    const displayedSeasons = useMemo(() => {
        return selectedSeason === 'all' ? sezonOptions : [selectedSeason];
    }, [selectedSeason, sezonOptions]);

    // Toplam değerleri hesaplama
    const totalStats = useMemo(() => {
        const dailyTotal = displayedSeasons.reduce((sum, sezon) => {
            const s = getStat(sezon);
            return sum + s.gunlukYeniKayit + s.gunlukKayitYenileme;
        }, 0);

        const grandTotal = displayedSeasons.reduce((sum, sezon) => {
            const s = getStat(sezon);
            return sum + s.toplamYeniKayit + s.toplamKayitYenileme;
        }, 0);

        return { dailyTotal, grandTotal };
    }, [displayedSeasons, statsBySezon]);

    // Özet kart bileşeni
    const SummaryCard: React.FC<SummaryCardProps> = ({ title, value, color, trendValue, icon }) => (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
        >
            <Card
                elevation={0}
                sx={{
                    height: '100%',
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '1px solid rgba(0, 0, 0, 0.05)',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        boxShadow: '0 5px 15px rgba(0, 0, 0, 0.08)',
                        transform: 'translateY(-3px)'
                    }
                }}
            >
                <CardContent sx={{ p: 3 }}>
                    <Box display="flex" alignItems="center" mb={1}>
                        <Box
                            sx={{
                                backgroundColor: `${color}`,
                                borderRadius: '50%',
                                width: 40,
                                height: 40,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mr: 2
                            }}
                        >
                            {icon || <TrendingUpIcon sx={{ color: 'white' }} />}
                        </Box>
                        <Typography variant="subtitle1" color="text.secondary">
                            {title}
                        </Typography>
                    </Box>
                    <Typography variant="h4" component="div" fontWeight="bold" mb={1}>
                        {value}
                    </Typography>
                    <Box display="flex" alignItems="center">
                        <TrendingUpIcon fontSize="small" sx={{ color: 'success.main', mr: 0.5 }} />
                        <Typography variant="body2" color="success.main">
                            {trendValue}% artış
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        </motion.div>
    );

    // Tablo bileşeni
    const StatTable: React.FC<{ sezon: number; color: string }> = ({ sezon, color }) => {
        const s = getStat(sezon);

        return (
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
            >
                <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: '1px solid rgba(0, 0, 0, 0.05)',
                        height: '100%',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                            boxShadow: '0 5px 15px rgba(0, 0, 0, 0.08)'
                        }
                    }}
                >
                    <Table size={isMobile ? "small" : "medium"}>
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    colSpan={5}
                                    align="center"
                                    sx={{
                                        backgroundColor: color,
                                        color: 'white',
                                        py: 2,
                                        fontSize: '1.1rem',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {sezon - 1} - {sezon} Kayıt İstatistikleri
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Kayıt Türü</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Bugün</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Bu Hafta</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Bu Ay</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Toplam</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Yeni Kayıt</TableCell>
                                <TableCell align="center">{s.gunlukYeniKayit}</TableCell>
                                <TableCell align="center">{s.haftalikYeniKayit}</TableCell>
                                <TableCell align="center">{s.aylikYeniKayit}</TableCell>
                                <TableCell
                                    align="center"
                                    sx={{ fontWeight: 'bold', color }}
                                >
                                    {s.toplamYeniKayit}
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Kayıt Yenileme</TableCell>
                                <TableCell align="center">{s.gunlukKayitYenileme}</TableCell>
                                <TableCell align="center">{s.haftalikKayitYenileme}</TableCell>
                                <TableCell align="center">{s.aylikKayitYenileme}</TableCell>
                                <TableCell
                                    align="center"
                                    sx={{ fontWeight: 'bold', color }}
                                >
                                    {s.toplamKayitYenileme}
                                </TableCell>
                            </TableRow>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell sx={{ fontWeight: 'bold' }}>Toplam</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                    {s.gunlukYeniKayit + s.gunlukKayitYenileme}
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                    {s.haftalikYeniKayit + s.haftalikKayitYenileme}
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                    {s.aylikYeniKayit + s.aylikKayitYenileme}
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{ fontWeight: 'bold', fontSize: '1.1rem', color }}
                                >
                                    {s.toplamYeniKayit + s.toplamKayitYenileme}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </motion.div>
        );
    };

    // Add institution name display
    const institutionName = selectedSubInstitution ? selectedSubInstitution.name : institution.name;

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert
                severity="error"
                sx={{ mt: 2, borderRadius: 2 }}
                action={
                    <>
                        {process.env.NODE_ENV === 'development' && (
                            <Typography variant="caption" display="block">
                                CorrelationId: {(error as KayitIstatistikleriError).correlationId}
                            </Typography>
                        )}
                        <IconButton color="inherit" size="small" onClick={() => refetch()}>
                            <RefreshIcon />
                        </IconButton>
                    </>
                }
            >
                {(error as Error).message}
            </Alert>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
                <Box>
                    <Typography
                        variant="h5"
                        component="h2"
                        sx={{
                            fontWeight: 'bold',
                            color: theme.palette.primary.main
                        }}
                    >
                        Kayıt İstatistikleri
                    </Typography>
                    {/* Display current institution name */}
                    <Typography
                        variant="subtitle1"
                        color="text.secondary"
                    >
                        {institutionName}
                    </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={2}>
                    {!isControlled && (
                        <ToggleButtonGroup
                            value={selectedSeason}
                            exclusive
                            onChange={(e, newSeason) => {
                                if (newSeason !== null) setSelectedSeason(newSeason);
                            }}
                            size="small"
                            color="primary"
                        >
                            <ToggleButton value="all">Tüm Sezonlar</ToggleButton>
                            {sezonOptions.map((sezon) => (
                                <ToggleButton key={sezon} value={sezon}>
                                    {sezon - 1} - {sezon}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    )}

                    <Tooltip title="Yenile">
                        <IconButton
                            onClick={() => refetch()}
                            color="primary"
                            disabled={isRefetching}
                            sx={{
                                animation: isRefetching ? 'spin 1s linear infinite' : 'none',
                                '@keyframes spin': {
                                    '0%': { transform: 'rotate(0deg)' },
                                    '100%': { transform: 'rotate(360deg)' }
                                }
                            }}
                        >
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Özet Kartları */}
            <Grid container spacing={3} mb={4}>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                    <SummaryCard
                        title="Toplam Kayıt"
                        value={totalStats.grandTotal}
                        color="#6200EA"
                        trendValue={8.5}
                    />
                </Grid>

                {displayedSeasons.map((sezon, index) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={sezon}>
                        <SummaryCard
                            title={`${sezon - 1} - ${sezon} Toplam`}
                            value={getStat(sezon).toplamYeniKayit + getStat(sezon).toplamKayitYenileme}
                            color={SEZON_RENKLERI[index % SEZON_RENKLERI.length]}
                            trendValue={12.3}
                        />
                    </Grid>
                ))}

                <Grid item xs={12} sm={6} md={4} lg={3}>
                    <SummaryCard
                        title="Bugünkü Kayıtlar"
                        value={totalStats.dailyTotal}
                        color="#4CAF50"
                        trendValue={15.2}
                    />
                </Grid>
            </Grid>

            {/* İstatistik Tabloları */}
            <Grid container spacing={3}>
                {displayedSeasons.map((sezon, index) => (
                    <Grid item xs={12} md={displayedSeasons.length === 1 ? 12 : 6} key={sezon}>
                        <StatTable sezon={sezon} color={SEZON_RENKLERI[index % SEZON_RENKLERI.length]} />
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

export default KayitIstatistikleri;
